<script lang="ts" setup>
import { computed, onMounted } from 'vue';
import {
  CdrContainer,
  CdrText,
  CdrButton,
  CdrList,
  CdrLink,
} from '@rei/cedar';
import OrderLookupForm from '@components/order-lookup-form.vue';
import useSearchParams from '@composables/use-search-params.mts';
import { recordView, setupAnalyticsClicks } from '@utils/analytics.mts';
import { URL_CONTACT_US, URL_LOGIN_TO_ORDER_LOOKUP } from '@utils/constants.mts';

const analytics = {
  signIn: 'order lookup: sign in',
};

const searchParams = useSearchParams();

const orderId = computed(() => {
  const orderIdParam = searchParams.orderId;

  // orderId cannot have more than 11 digits when they DO NOT start with A and Y
  if (
    orderIdParam?.length > 11
    && !orderIdParam?.startsWith('A')
    && !orderIdParam?.startsWith('Y')
  ) {
    return '';
  }

  return orderIdParam;
});

onMounted(async () => {
  recordView({
    templateType: 'purchase history',
    pageDescription: 'order tracking_unregistered',
    orderHistoryId: orderId.value,
  });
  setupAnalyticsClicks();
});
</script>

<template>
  <cdr-container
    class="order-lookup"
    data-ui="order-lookup"
  >
    <cdr-text
      tag="h1"
      class="order-lookup__title"
    >
      Order Status
    </cdr-text>
    <cdr-text>
      Sign in to see purchases you've made with your member number or while signed in.
    </cdr-text>
    <cdr-button
      v-analytics="analytics.signIn"
      class="order-lookup__sigin-button"
      data-ui="order-lookup--sign-in-button"
      tag="a"
      :href="URL_LOGIN_TO_ORDER_LOOKUP"
    >
      Sign in
    </cdr-button>
    <order-lookup-form
      class="order-lookup__form"
      container-page="order-lookup"
      :order-id="orderId"
    >
      <legend>
        <cdr-text
          tag="h2"
          class="order-lookup__subtitle"
        >
          Look up an individual order
        </cdr-text>
        <cdr-text class="order-lookup__slot-message">
          Use this form to find orders placed over the phone, with in-store staff for delivery or
          via guest checkout online.
        </cdr-text>
      </legend>
    </order-lookup-form>
    <hr>
    <section class="order-lookup__message">
      <cdr-text
        tag="h2"
        class="order-lookup__subtitle"
      >
        Can't find a purchase?
      </cdr-text>
      <cdr-text> The forms above won't be able to find: </cdr-text>
      <cdr-list tag="ul">
        <li>
          Purchases made in stores without a member number (unless an employee placed it for you)
        </li>
        <li>Classes, events, trips or rentals</li>
        <li>Re/Supply used gear purchases</li>
      </cdr-list>
      <cdr-text>
        If you need help finding one of these order types, please
        <cdr-link
          data-ui="order-lookup--link-contact-us"
          :href="URL_CONTACT_US"
        >
          contact us
        </cdr-link>.
      </cdr-text>
    </section>
  </cdr-container>
  <minitray-plugin />
</template>

<style lang="scss" scoped>
.order-lookup {
  display: flex;
  justify-content: center;
  flex-direction: column;
  margin-block: $cdr-space-two-x;
  padding: 0;
  max-width: 400px;

  @include cdr-xs-mq-only {
    padding-inline: $cdr-space-one-x;
  }

  &__sigin-button {
    margin-top: $cdr-space-three-quarter-x;
    width: 84px;
  }

  &__title {
    @include cdr-text-heading-serif-900;

    margin-bottom: $cdr-space-three-quarter-x;
  }

  &__slot-message {
    margin-bottom: $cdr-space-one-and-a-half-x;
  }

  &__message {
    margin-block: $cdr-space-two-x $cdr-space-four-x;
  }

  &__subtitle {
    @include cdr-text-subheading-sans-600;

    margin-bottom: $cdr-space-one-x;
  }

  &__form {
    padding-top: $cdr-space-three-x;
  }

  p {
    @include cdr-text-utility-sans-300;
  }

  hr {
    margin-top: $cdr-space-one-and-a-half-x;
    color: $cdr-color-border-primary;
    border: 1px solid;
    border-bottom: none;
    width: 400px;
  }
}
</style>
